import { DirectoryUser, ProfileDirectoryService } from '../../shared/services/profile-routing/profile-directory.service';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DwOrmDataService, DwOrmDataServiceToken, DwSecurityUserService } from '@devwareapps/devware-cap';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, finalize, map, startWith, switchMap, take } from 'rxjs/operators';
import { ChallengeContextService } from '../../shared/contexts/challenge-context/challenge-context.service';
import { AppMetaData, UserProfileEntity, UserChallengeEntity, UserChallengeFriendEntity } from '../../../meta-data/app-meta-data.service';

export interface FriendOption { id: number; name: string; person?: DirectoryUser; }
export interface ProfileFriend extends UserChallengeFriendEntity { name: string; person?: DirectoryUser; }
export interface ProfileFriendsState {
    loading: boolean; error: string; enrollmentId: number; friends: ProfileFriend[]; options: FriendOption[];
}
const initial = (): ProfileFriendsState => ({ loading: true, error: '', enrollmentId: 0, friends: [], options: [] });
@Injectable()
export class ProfileFriendsService implements OnDestroy {
    private subject = new BehaviorSubject<ProfileFriendsState>(initial());
    readonly state$ = this.subject.asObservable();
    private refresh$ = new Subject<void>();
    private subscriptions = new Subscription();
    private generation = 0;
    private refreshAfterSave = false;
    saving = false;
    uncertain = false;
    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
        private security: DwSecurityUserService, selection: ChallengeContextService, private directory: ProfileDirectoryService) {
        this.subscriptions.add(combineLatest([selection.challengeChange$,
            security.securityContextChanged().pipe(startWith(null)), this.refresh$.pipe(startWith(null))])
            .pipe(switchMap(([selected]) => {
                this.generation++; this.subject.next(initial());
                const user = this.security.securityContext && this.security.securityContext.ApplicationUser;
                if (!user || !selected.ChallengeDefinitionId) {
                    return of({ ...initial(), loading: false, error: 'Select a challenge and sign in to manage friends.' });
                }
                return this.loadProfile(user.UserId).pipe(switchMap(profiles => {
                    if (profiles.length !== 1) { throw new Error('Profile unavailable.'); }
                    return this.loadEnrollment(profiles[0].UserProfileId, selected.ChallengeDefinitionId);
                }), switchMap(enrollments => {
                    if (enrollments.length !== 1) { throw new Error('Completed registration is required for this challenge.'); }
                    const enrollmentId = enrollments[0].UserChallengeId;
                    return combineLatest([
                        this.loadFriends(enrollmentId),
                        this.loadCandidates(selected.ChallengeDefinitionId),
                        this.directory.get(selected.ChallengeDefinitionId).pipe(take(1)),
                    ]).pipe(map(([friends, candidates, directory]): ProfileFriendsState => ({ loading: false, error: '', enrollmentId,
                        friends: friends.map(friend => {
                            const person = directory.users.find(user => user.UserChallengeId === friend.FriendUserChallengeId);
                            return { ...friend, person, name: this.name(person) };
                        }).sort((a, b) => a.name.localeCompare(b.name)),
                        options: candidates.filter(c => c.UserChallengeId !== enrollmentId &&
                            !friends.some(f => f.FriendUserChallengeId === c.UserChallengeId))
                            .map(c => {
                                const person = directory.users.find(user => user.UserChallengeId === c.UserChallengeId);
                                return { id: c.UserChallengeId, person, name: this.name(person) };
                            })
                            .sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id),
                    })));
                }), catchError(() => of({ ...initial(), loading: false,
                    error: 'Unable to load friends. Completed registration and profile access are required. Try refreshing.' })));
            })).subscribe(state => this.subject.next(state)));
    }
    private name(person: DirectoryUser): string { return [person?.FirstName, person?.LastName].filter(Boolean).join(' ').trim() || 'Streaker'; }
    refresh(): void {
        if (this.saving) { this.refreshAfterSave = true; return; }
        this.uncertain = false; this.refresh$.next();
    }
    add(option: FriendOption): Observable<void> {
        return this.mutate(state => {
            if (!state.options.some(c => c.id === option.id) || option.id === state.enrollmentId) {
                throw new Error('This Streaker is no longer available. Refresh friends.');
            }
            const entity = AppMetaData.UserChallengeFriend.CreateEntity();
            entity.UserChallengeId = state.enrollmentId; entity.FriendUserChallengeId = option.id;
            entity.CanEnterActivities = false;
            return this.orm.saveEntity(entity).pipe(map(() => undefined));
        });
    }
    remove(id: number): Observable<void> {
        return this.mutate(state => {
            const friend = state.friends.find(f => f.UserChallengeFriendId === id);
            if (!friend) { throw new Error('This friend is no longer in the list. Refresh friends.'); }
            const entity = AppMetaData.UserChallengeFriend.CreateEntity();
            entity.UserChallengeFriendId = id;
            return this.orm.deleteEntity(entity).pipe(map(() => undefined));
        });
    }
    private mutate(operation: (state: ProfileFriendsState) => Observable<void>): Observable<void> {
        return defer(() => {
            const state = this.subject.value;
            if (this.saving || this.uncertain || state.loading || state.error || !state.enrollmentId) {
                return throwError(new Error('Refresh friends before changing the list.'));
            }
            const version = this.generation;
            this.saving = true;
            return defer(() => operation(state)).pipe(take(1), catchError(error => {
                if (version === this.generation) { this.uncertain = true; }
                return throwError(error);
            }), finalize(() => {
                this.saving = false;
                if (this.refreshAfterSave) {
                    this.refreshAfterSave = false;
                    if (version === this.generation) { this.refresh(); }
                }
            }));
        });
    }
    private loadProfile(userId: number): Observable<UserProfileEntity[]> {
        const query = AppMetaData.UserProfile.CreateQueryBuilder().addFields(a => [a.UserProfileId])
            .addFilterAnd(f => f.Equal(a => a.UserId, userId));
        return this.orm.executeQuery<UserProfileEntity>(query).pipe(take(1));
    }
    private loadEnrollment(profileId: number, challengeId: number): Observable<UserChallengeEntity[]> {
        const query = AppMetaData.UserChallenge.CreateQueryBuilder().addFields(a => [a.UserChallengeId])
            .addFilterAnd(f => f.Equal(a => a.UserProfileId, profileId))
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.IsRegistrationComplete, true));
        return this.orm.executeQuery<UserChallengeEntity>(query).pipe(take(1));
    }
    private loadFriends(enrollmentId: number): Observable<UserChallengeFriendEntity[]> {
        const query = AppMetaData.UserChallengeFriend.CreateQueryBuilder()
            .addFields(a => [a.UserChallengeFriendId, a.UserChallengeId, a.FriendUserChallengeId])
            .addFilterAnd(f => f.Equal(a => a.UserChallengeId, enrollmentId));
        return this.orm.executeQuery<UserChallengeFriendEntity>(query).pipe(take(1));
    }
    private loadCandidates(challengeId: number): Observable<UserChallengeEntity[]> {
        const query = AppMetaData.UserChallenge.CreateQueryBuilder().addFields(a => [a.UserChallengeId, a.UserProfileId])
            .addFilterAnd(f => f.Equal(a => a.ChallengeDefinitionId, challengeId))
            .addFilterAnd(f => f.Equal(a => a.IsRegistrationComplete, true));
        return this.orm.executeQuery<UserChallengeEntity>(query).pipe(take(1));
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
}
